/** @format */

import React, {FC, useCallback, useEffect, useState, useLayoutEffect} from "react";
import {Linking, ScrollView, StyleSheet, Text, View} from "react-native";
import {Button, Spacer} from "../../components";
import {ButtonType, IsMobile} from "../../theme";
import {NavigationProp, useNavigation,} from "@react-navigation/native";
import {HomeTabsParams} from "../../navigation/AppStack/HomeTabParams";
import {Storage} from "../../utils/Storage";
import {WalletStackRoute} from "../../navigation/AppStack/routes";
import {AppTheme, useTheme} from "../../theme/colors";
import PageContainer from "../../components/PageContainer";
import CustomTextInput, {CustomTextInputChangeEvent,} from "../../components/CustomTextInput";
import {Analytics} from "aws-amplify";
import {useAppContext} from "../../contexts/AppContext";
import * as Clipboard from "expo-clipboard";
import {WalletScreenRoute} from "../../navigation/WalletStack/routes";

const PassportFileShareScreen: FC = () => {
  const [link, setLink] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isPasswordAlertVisibleText, setPasswordAlertVisibilityText] =
    useState("");
  const navigation = useNavigation<NavigationProp<HomeTabsParams>>();
  const theme = useTheme();
  const {passportFile} = useAppContext();

  const shareScreenStyles = getShareScreenStyles(theme);

  useEffect(() => {
    (async () => {
      setLink(() => passportFile?.url || "The link will appear here");

      let store = await Storage.getStorage();
      setPassword(() => store.password ?? "");
    })();
  }, []);



  const getEmailLink = useCallback(async () => {
    const sharepassword = (await Storage.getValue("password")) || "";
    const userEmail = (await Storage.getValue("email")) || "";
    const university = (await Storage.getValue("profile_university")) || "";

    Analytics.record({
      name: "SharePassportFile",
      attributes: {
        email: userEmail as string,
        university: university as string,
      },
    });

    // change it to whatever looks good
    const subject = "OneSpace Sharing Document";
    const body = `
    Dear Sir/Madam,

    As a trustworthy person, I'm sharing with you the document "${passportFile?.title}" with confidential details.

	${link}

    Please keep this document in a safe place and use the following password to open it:

    ${sharepassword}
    `;
    const mail = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    try {
      const canOpen = await Linking.canOpenURL(mail);
      if (canOpen) {
        await Linking.openURL(mail);
      }
    } catch (error) {
      // in the future we may need a proper alert modal
      alert(JSON.stringify(error));
    }
  }, [link, email]);

  const getPassword = useCallback(async () => {
    if (password) {
      await Clipboard.setStringAsync(password);
      setPasswordAlertVisibilityText("Password was copied to clipboard");
    } else
      setPasswordAlertVisibilityText("Password is not generated yet, please generate a new password");
    // showErrorModal(
    // 	"Password is not generated yet, please generate a new password"
    // );
  }, [password]);

  const onClickCancelButton = () => {
    navigation.navigate(WalletStackRoute, {
      screen: WalletScreenRoute
    });
  };

  const openSharingLink = () => {
    Linking.openURL(passportFile?.url!);
  };

  const onChangeEmailField = (event: CustomTextInputChangeEvent) => {
    setEmail(event.value);
  };

  return (
    <PageContainer>
      <View style={shareScreenStyles.viewContainer}>
        <View style={shareScreenStyles.headerView}>
          <Text style={shareScreenStyles.headerText}>
            {passportFile?.title} file
          </Text>
          <Button
            onPress={onClickCancelButton}
            title="Cancel"
            buttonTextStyles={shareScreenStyles.headerButton}
          />
        </View>
        <ScrollView
          style={{flex: 1}}
          scrollEventThrottle={1600}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingHorizontal: 10}}
        >
          <View style={shareScreenStyles.shareLinkView}>
            <Text style={shareScreenStyles.textParagraph}>
              Get a preview of what you can share by accessing the
              sharing link
            </Text>
            <View style={shareScreenStyles.center}>
              <Button
                title="Get Sharing Link"
                variant={ButtonType.secondary}
                onPress={openSharingLink}
              />
            </View>
          </View>

          <View style={shareScreenStyles.sharePassportView}>
            <Text style={shareScreenStyles.textParagraph}>
              Click the 'Share file' button to open an email pre-filled with the necessary details for sharing your file.
            </Text>
            <View>
              <View style={shareScreenStyles.center}>
                <Button
                  title={`Share file`}
                  variant={ButtonType.secondary}
                  onPress={getEmailLink}
                />
              </View>
            </View>
          </View>

          <View style={shareScreenStyles.sharePasswordView}>
            <Text style={shareScreenStyles.textParagraph}>
              Password to open the file {passportFile?.title}
            </Text>
            <CustomTextInput
              name="share_password"
              placeholder="Clear Password"
              defaultValue={password}
              fieldStyles={shareScreenStyles.sharePasswordField}
              editable={false}
            />
            <View style={shareScreenStyles.center}>
              <Button
                title="Copy Password"
                variant={ButtonType.secondary}
                onPress={getPassword}
              />
            </View>
            {isPasswordAlertVisibleText !== '' && (
              <Text
                style={shareScreenStyles.errorText}
                onLayout={() =>
                  setTimeout(() => {
                    setPasswordAlertVisibilityText("");
                  }, 2000)
                }
              >
                {isPasswordAlertVisibleText}
              </Text>
            )}
          </View>
          <Spacer/>
        </ScrollView>
      </View>
    </PageContainer>
  );
};

export default PassportFileShareScreen;

const getShareScreenStyles = (theme: AppTheme) => {
  return StyleSheet.create({
    viewContainer: {
      flex: 1,
      backgroundColor: theme.BACKGROUND,
      paddingTop: 23,
      paddingHorizontal: 15
    },
    headerView: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 15,
      paddingVertical: IsMobile ? 0 : 20,
    },
    headerText: {
      fontSize: 20,
      fontWeight: "600",
      lineHeight: 24,
      letterSpacing: 0.8,
      color: theme.TEXT,
    },
    headerButton: {
      color: theme.PRIMARY,
    },

    shareLinkView: {
      marginTop: 52,
    },

    sharePassportView: {
      marginTop: 60,
      marginBottom: 20,
    },
    sharePassportField: {
      marginBottom: 30,
      backgroundColor: theme.BACKGROUND,
      borderBottomColor: "#ddd",
      borderBottomWidth: 1,
      paddingVertical: 5,
      paddingHorizontal: 0,
    },

    sharePasswordView: {
      marginTop: 60,
      marginBottom: 20,
    },

    sharePasswordField: {
      marginBottom: 30,
      backgroundColor: theme.BACKGROUND,
      borderBottomColor: "#ddd",
      borderBottomWidth: 1,
      paddingVertical: 5,
      paddingHorizontal: 0,
    },

    textParagraph: {
      fontSize: 16,
      fontWeight: "400",
      lineHeight: 24,
      letterSpacing: 0.8,
      marginBottom: 30,
      color: theme.TEXT,
    },
    center: {
      alignItems: "center",
    },
    errorText: {
      color: theme.TEXT,
      marginTop: 10,
      textAlign: "center",
    },
  });
};
